import React, { useCallback, useEffect, useState, type Dispatch } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND, type TextFormatType } from 'lexical';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import TextBoldFilled from '@uber/icons/text-bold-filled';
import TextItalicFilled from '@uber/icons/text-italic-filled';
import TextStrikethroughFilled from '@uber/icons/text-strikethrough-filled';
import TextUnderlineFilled from '@uber/icons/text-underline-filled';
import ChevronLeftRightFilled from '@uber/icons/chevron-left-right-filled';
import LinkFilled from '@uber/icons/link-filled';
import { styled, useStyletron } from 'baseui';
import { getSelectedNode, sanitizeUrl } from '../../../utils/utils';
import { mergeRegister } from '@lexical/utils';
import { TEXT_STYLE } from '../constants/constants';
const ToolbarIcon = styled<'button', {
  $isSelected: boolean;
  disabled: boolean;
}>('button', ({
  $theme,
  $isSelected,
  disabled
}) => ({
  border: '0',
  display: 'flex',
  background: 'none',
  borderRadius: $theme.sizing.scale200,
  padding: $theme.sizing.scale300,
  ...($isSelected && {
    backgroundColor: $theme.colors.backgroundTertiary
  }),
  cursor: disabled ? 'not-allowed' : 'pointer',
  verticalAlign: 'middle',
  color: disabled ? $theme.colors.inputTextDisabled : $theme.colors.backgroundInversePrimary,
  ...(!disabled && {
    ':hover': {
      backgroundColor: $theme.colors.backgroundTertiary
    }
  })
}));
const TextStyleOptions = ({
  setIsLinkEditMode
}: {
  setIsLinkEditMode: Dispatch<boolean>;
}) => {
  const [editor] = useLexicalComposerContext();
  const [textStyles, setTextStyles] = useState<TEXT_STYLE[]>([]);
  const [isLink, setIsLink] = useState<boolean>(false);
  const [isEditable, setIsEditable] = useState<boolean>(() => editor.isEditable());
  const [, theme] = useStyletron();
  const getIconColor = () => {
    return isEditable ? theme.colors.backgroundInversePrimary : theme.colors.inputTextDisabled;
  };
  const formatText = (formatType: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType);
  };
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setTextStyles(Object.values(TEXT_STYLE).filter(style => selection.hasFormat(style)));
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
    }
  }, []);
  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl('https://'));
    } else {
      setIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink, setIsLinkEditMode]);
  useEffect(() => {
    return mergeRegister(editor.registerEditableListener(editable => {
      setIsEditable(editable);
    }), editor.registerUpdateListener(({
      editorState
    }) => {
      editorState.read(() => {
        updateToolbar();
      });
    }));
  }, [updateToolbar, editor]);
  return <>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.BOLD)} onClick={() => {
      formatText(TEXT_STYLE.BOLD);
    }} data-testid="boldButton">
        <TextBoldFilled color={getIconColor()} />
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.ITALIC)} onClick={() => {
      formatText(TEXT_STYLE.ITALIC);
    }} data-testid="italicButton">
        <TextItalicFilled color={getIconColor()} />
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.UNDERLINE)} onClick={() => {
      formatText(TEXT_STYLE.UNDERLINE);
    }} data-testid="underlineButton">
        <TextUnderlineFilled color={getIconColor()} />
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.STRIKETHROUGH)} onClick={() => {
      formatText(TEXT_STYLE.STRIKETHROUGH);
    }} data-testid="strikethroughButton">
        <TextStrikethroughFilled color={getIconColor()} />
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.CODE)} onClick={() => {
      formatText(TEXT_STYLE.CODE);
    }} data-testid="codeButton">
        <ChevronLeftRightFilled color={getIconColor()} />
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.SUBSCRIPT)} onClick={() => {
      formatText(TEXT_STYLE.SUBSCRIPT);
    }} data-testid="subscriptButton">
        subscript
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={textStyles.includes(TEXT_STYLE.SUPERSCRIPT)} onClick={() => {
      formatText(TEXT_STYLE.SUPERSCRIPT);
    }} data-testid="superscriptButton">
        superscript
      </ToolbarIcon>
      <ToolbarIcon disabled={!isEditable} $isSelected={isLink} onClick={insertLink} data-testid="linkButton">
        <LinkFilled color={getIconColor()} />
      </ToolbarIcon>
    </>;
};
export default TextStyleOptions;